import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Button, Tooltip } from '@mui/material';
import { useEnqueueSnackbar } from 'hooks';
import { queryClient } from 'lib/react-query';
import type { PackEntry } from 'types/shared';
import queryKeys from '../api/queryKeys';
import type { HypothesisCreativeEntry } from '../types/hypothesis';

const NO_HYPOTHESIS_TAG = 'No hypothesis';

const AddTestingPackButton = () => {
  const { onShowAlert } = useEnqueueSnackbar();
  const { watch, setValue, trigger } = useFormContext();
  const selectedCreatives = watch('selectedCreatives') as string[];
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];

  const isAddPackAvailable = !!selectedCreatives.length;

  const handleAddPack = useCallback(() => {
    const cachedTestCreatives = queryClient
      .getQueryCache()
      .findAll({ queryKey: queryKeys.allTestCreatives })
      .flatMap((query) => query.state.data as HypothesisCreativeEntry[])
      .filter((c): c is HypothesisCreativeEntry => !!c);

    const idToHypothesis = new Map<string, string>();
    for (const creative of cachedTestCreatives) {
      if (!idToHypothesis.has(creative.id)) {
        idToHypothesis.set(creative.id, creative.hypothesis ?? NO_HYPOTHESIS_TAG);
      }
    }

    const groupedByHypothesis = new Map<string, string[]>();
    for (const id of selectedCreatives) {
      const hypothesis = idToHypothesis.get(id) ?? NO_HYPOTHESIS_TAG;
      const existing = groupedByHypothesis.get(hypothesis);
      if (existing) {
        existing.push(id);
      } else {
        groupedByHypothesis.set(hypothesis, [id]);
      }
    }

    const newPacks: PackEntry[] = [...groupedByHypothesis.entries()].map(
      ([tag, creatives]) => ({
        tag,
        adType: '',
        creatives,
        filterCreativeByName: [],
        visual_touch_ups: true,
      }),
    );

    if (!newPacks.length) return;

    const updatedPacks = [...packsWithCreatives, ...newPacks];
    setValue('packsWithCreatives', updatedPacks, { shouldValidate: true });
    setValue('activePacksWithCreatives', packsWithCreatives.length);
    setValue('selectedCreatives', [], { shouldValidate: true });

    onShowAlert(
      newPacks.length === 1 ? 'Pack added' : `${newPacks.length} packs added`,
      { delayAlert: 3500 },
    );

    void trigger('packsWithCreatives');
  }, [
    selectedCreatives,
    packsWithCreatives,
    setValue,
    trigger,
    onShowAlert,
  ]);

  return (
    <Tooltip title={'Select creatives to add a new pack'} placement={'top'}>
      <Box>
        <Button
          disabled={!isAddPackAvailable}
          variant="outlined"
          color="primary"
          onClick={handleAddPack}
        >
          Add pack
        </Button>
      </Box>
    </Tooltip>
  );
};

export default AddTestingPackButton;
